
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/base/Button';
import TopNavigation from '../../components/feature/TopNavigation';
import BottomNavigation from '../../components/feature/BottomNavigation';

export default function PaymentPage() {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [selectedPayment, setSelectedPayment] = useState('');
  const [loading, setLoading] = useState(false);

  const plans = [
    {
      id: 'monthly',
      name: 'الاشتراك الشهري',
      price: '29',
      period: 'شهرياً',
      features: ['بحثات غير محدودة', 'دعم فني 24/7', 'نتائج فورية'],
      popular: true
    },
    {
      id: 'yearly',
      name: 'الاشتراك السنوي',
      price: '299',
      period: 'سنوياً',
      originalPrice: '348',
      features: ['بحثات غير محدودة', 'دعم فني 24/7', 'نتائج فورية', 'توفير 15%'],
      popular: false
    },
    {
      id: 'payper',
      name: 'الدفع لكل بحث',
      price: '5',
      period: 'لكل بحث',
      features: ['دفع حسب الاستخدام', 'لا التزام شهري', 'مثالي للاستخدام المحدود'],
      popular: false
    }
  ];

  const paymentMethods = [
    { id: 'mada', name: 'مدى', icon: 'ri-bank-card-line' },
    { id: 'visa', name: 'فيزا / ماستركارد', icon: 'ri-bank-card-line' },
    { id: 'applepay', name: 'Apple Pay', icon: 'ri-apple-line' }
  ];

  const handlePayment = async () => {
    if (!selectedPayment) return;
    
    setLoading(true);
    
    // Simulate payment processing
    setTimeout(() => {
      setLoading(false);
      // Show success and navigate
      navigate('/home');
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <TopNavigation title="الاشتراك والدفع" showBack={true} />
      
      <div className="pt-16 px-6 py-8">
        {/* Current Status */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">الخطة الحالية</h3>
              <p className="text-gray-600">مجاني - 3 بحثات متبقية</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <i className="ri-vip-crown-line text-purple-600 text-xl w-6 h-6 flex items-center justify-center"></i>
            </div>
          </div>
        </div>

        {/* Plans */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">اختر خطتك</h2>
          <div className="space-y-4">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative bg-white rounded-xl shadow-sm border-2 transition-all ${
                  selectedPlan === plan.id
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-medium px-3 py-1 rounded-full">
                      الأكثر شعبية
                    </span>
                  </div>
                )}
                
                <button
                  onClick={() => setSelectedPlan(plan.id)}
                  className="w-full p-6 text-right"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3 rtl:space-x-reverse">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedPlan === plan.id
                          ? 'border-purple-600 bg-purple-600'
                          : 'border-gray-300'
                      }`}>
                        {selectedPlan === plan.id && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                      <h3 className="font-semibold text-gray-900">{plan.name}</h3>
                    </div>
                    <div className="text-left">
                      <div className="flex items-baseline space-x-1">
                        <span className="text-2xl font-bold text-gray-900">{plan.price}</span>
                        <span className="text-gray-600">ريال</span>
                      </div>
                      {plan.originalPrice && (
                        <span className="text-sm text-gray-500 line-through">{plan.originalPrice} ريال</span>
                      )}
                      <p className="text-sm text-gray-600">{plan.period}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2 rtl:space-x-reverse">
                        <i className="ri-check-line text-green-600 text-sm w-4 h-4 flex items-center justify-center"></i>
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Methods */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">طريقة الدفع</h2>
          <div className="space-y-3">
            {paymentMethods.map((method) => (
              <button
                key={method.id}
                onClick={() => setSelectedPayment(method.id)}
                className={`w-full p-4 bg-white rounded-lg border-2 transition-all ${
                  selectedPayment === method.id
                    ? 'border-purple-600 bg-purple-50'
                    : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 rtl:space-x-reverse">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedPayment === method.id
                        ? 'border-purple-600 bg-purple-600'
                        : 'border-gray-300'
                    }`}>
                      {selectedPayment === method.id && (
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      )}
                    </div>
                    <span className="font-medium text-gray-900">{method.name}</span>
                  </div>
                  <i className={`${method.icon} text-gray-600 text-xl w-6 h-6 flex items-center justify-center`}></i>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4">ملخص الطلب</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">الخطة المختارة</span>
              <span className="font-medium">
                {plans.find(p => p.id === selectedPlan)?.name}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">السعر</span>
              <span className="font-medium">
                {plans.find(p => p.id === selectedPlan)?.price} ريال
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">الضريبة (15%)</span>
              <span className="font-medium">
                {Math.round(parseInt(plans.find(p => p.id === selectedPlan)?.price || '0') * 0.15)} ريال
              </span>
            </div>
            <hr className="my-2" />
            <div className="flex justify-between text-lg font-semibold">
              <span>المجموع</span>
              <span>
                {Math.round(parseInt(plans.find(p => p.id === selectedPlan)?.price || '0') * 1.15)} ريال
              </span>
            </div>
          </div>
        </div>

        {/* Pay Button */}
        <Button
          onClick={handlePayment}
          className="w-full"
          size="lg"
          loading={loading}
          disabled={!selectedPayment}
        >
          {loading ? 'جارٍ المعالجة...' : 'ادفع الآن'}
        </Button>

        {/* Terms */}
        <p className="text-xs text-gray-600 text-center mt-4">
          بالمتابعة، أنت توافق على{' '}
          <button className="text-purple-600 underline">الشروط والأحكام</button>
          {' '}و{' '}
          <button className="text-purple-600 underline">سياسة الخصوصية</button>
        </p>
      </div>

      <BottomNavigation />
    </div>
  );
}
